import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import TextInput from 'components/Inputs/TextInput'
import LoyaltyCardList from 'components/Lists/LoyaltyCardList'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AddLoyaltyCardModal from 'pages/Modals/AddLoyaltyCardModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  Container,
  Row,
  InfoText,
  LinkText
} from '../styles'

export default function EditLoyaltyCards () {
  const [loading, setLoading] = useState (true)
  const [loyaltyCards, setLoyaltyCards] = useState ([])
  const [showAddCardModal, setShowAddCardModal] = useState (false)
  const [name, setName] = useState ('')
  const [status, setStatus] = useState ({label: 'Ativos', value: 'active'})
  const now = DateTime.now ()
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getLoyaltyCards ()
  }, [name, status])

  const getLoyaltyCards = async () => {
    let filters = `status=${status.value}`
    if (name) filters += `&name=${encodeURIComponent (name)}`

    try {
      setLoading (true)

      const { data: loyaltyCards } = await cutileiApi.get (
        `/businesses/${businessId}/loyalty_cards?${filters}`, requestConfig
      )

      setLoyaltyCards (loyaltyCards)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const parseISODate = isoDate => {
    if (!isoDate) return null
    return DateTime.fromISO (isoDate)
  }

  const compareCards = (a, b) => (
    parseISODate (a.created_at).toMillis () - parseISODate (b.created_at).toMillis ()
  )

  const handleEdit = card => {
    if (
      status.value === 'active' && card.active === false ||
      status.value === 'inactive' && card.active === true ||
      status.value === 'expired' && (!card.expires_at || parseISODate (card.expires_at) >= now) ||
      status.value !== 'expired' && (!!card.expires_at && parseISODate (card.expires_at) < now)
    ) {
      handleDelete (card)
      return
    }
    setLoyaltyCards (loyaltyCards.map (c => c.id === card.id ? {...card} : c))
  }
  
  const handleDelete = card => {
    setLoyaltyCards (loyaltyCards.filter (c => c.id !== card.id))
  }

  const toggleAddCardModal = () => setShowAddCardModal (!showAddCardModal)

  return (
    <Container>
      {isTutorial && (
        <InfoText style={{marginTop: -5, marginBottom: 15}}>
          <LinkText to='/business'>
            {'Clique aqui '}
          </LinkText>
          para voltar para o <b>guia de configuração do salão</b>
        </InfoText>
      )}
      <Row style={{gap: 10, width: 600}}>
        <Select
          name='status'
          placeholder='Status...'
          value={status}
          options={[
            {label: 'Ativos', value: 'active'},
            {label: 'Inativos', value: 'inactive'},
            {label: 'Vencidos', value: 'expired'},
            {label: 'Excluídos', value: 'deleted'}
          ]}
          onChange={(_, option) => setStatus (option)}
        />
        <TextInput
          value={name}
          placeholder='Pesquisar por nome'
          isClearable={true}
          onChange={e => setName (e.target.value)}
          onClear={() => setName ('')}
          inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
        />
      </Row>
      {AuthService.hasPermission ('business_config.loyalty_cards', 'edit') && (
        <Row style={{gap: 10, marginBottom: 20}}>
          <SecondaryButton
            label='Criar cartão fidelidade'
            onClick={toggleAddCardModal}
            width={190}
          />
          <AddLoyaltyCardModal
            visible={showAddCardModal}
            onConfirm={addedCard => setLoyaltyCards (
              [...loyaltyCards, addedCard].sort (compareCards)
            )}
            onClose={toggleAddCardModal}
          />
        </Row>
      )}
      {loading ? <Loading/> : (
        <LoyaltyCardList
          cards={loyaltyCards}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </Container>
  )
}
